import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { MuiTable, TableCell } from "src/components/MuiTable/MuiTable.tsx";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import type {
  ReplenishmentConversionResult,
  ReplenishmentSuggestionResource,
  ReplenishmentSupplierSummary,
} from "@exonn/js-sdk";
import { Panel } from "src/components/Panel/Panel.tsx";
import { Button } from "src/components/Button/Button.tsx";
import { Input } from "src/components/Input/Input.tsx";
import { Flex } from "src/components/Flex.tsx";
import { TextV2 } from "src/components/TextV2/TextV2.tsx";
import { Alert } from "src/components/Alert/Alert.tsx";
import { useExonnApi } from "src/exonn-api/exonn-api.tsx";
import { useSnackbar } from "src/notifications/SnackbarProvider.ts";
import { ResourceEnum } from "src/constants/constants.ts";
import { useResourceFormTranslation } from "src/components/Form/hooks/useResourceFormTranslation.ts";
import { useRunProgress } from "src/admin/modules/replenishment-run/forms/replenishment-run/hooks/useRunProgress.ts";
import { ConvertDialog } from "src/admin/modules/replenishment-run/forms/replenishment-run/tabs/suggestions/ConvertDialog.tsx";

const quantity = (value: string | null) =>
  value === null ? "—" : Number(value).toLocaleString("de-DE");
const money = (value: string) =>
  Number(value).toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const serverMessage = (error: unknown, fallback: string) => {
  if (!isAxiosError(error)) {
    return fallback;
  }
  const errors = error.response?.data?.errors as
    | Record<string, string[]>
    | undefined;

  return (
    (errors ? Object.values(errors)[0]?.[0] : undefined) ??
    error.response?.data?.message ??
    fallback
  );
};

/**
 * Bestellvorschläge прогона, сгруппированные по поставщику: снимок расчёта по варианту,
 * итоговое количество (правится, сохраняется при выходе из поля), отклонение и сумма
 * поставщика против Mindestbestellwert. «Bestellungen erstellen» — черновики заказов.
 */
export const Suggestions = () => {
  const { t } = useResourceFormTranslation();
  const exonnApi = useExonnApi();
  const snackbar = useSnackbar();
  const queryClient = useQueryClient();
  const { run, isRunning } = useRunProgress();
  const [converting, setConverting] = useState(false);
  const [result, setResult] = useState<ReplenishmentConversionResult | null>(
    null,
  );

  const ready = !!run?.id && run.status === "completed";
  const key = [ResourceEnum.ReplenishmentRun, run?.id, "suggestions"];

  const suggestions = useQuery({
    queryKey: [ResourceEnum.ReplenishmentRun, run?.id, "suggestions", "list"],
    enabled: ready,
    queryFn: async () =>
      (
        await exonnApi.client.get<{ data: ReplenishmentSuggestionResource[] }>(
          "/replenishment-suggestion",
          {
            params: {
              include: "variant,supplier",
              "page[size]": 10_000,
              "filter[items][0][field]": "replenishment_run_id",
              "filter[items][0][operator]": "is",
              "filter[items][0][value]": run?.id,
            },
          },
        )
      ).data.data,
  });
  const summary = useQuery({
    queryKey: [
      ResourceEnum.ReplenishmentRun,
      run?.id,
      "suggestions",
      "summary",
    ],
    enabled: ready,
    queryFn: async () =>
      (
        await exonnApi.client.get<{ data: ReplenishmentSupplierSummary[] }>(
          `/replenishment-run/${run?.id}/suppliers`,
        )
      ).data.data,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: key });
  const onError = (error: unknown) =>
    snackbar.error({
      flash: true,
      message: serverMessage(error, t("notifications.action-failed")),
    });

  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, string> }) =>
      exonnApi.client.put(`/replenishment-suggestion/${id}`, body),
    onSuccess: refresh,
    onError,
  });
  const convert = useMutation({
    mutationFn: async (body: {
      owner_profile_id: string;
      warehouse_id?: string;
    }) =>
      (
        await exonnApi.client.post<{ data: ReplenishmentConversionResult }>(
          `/replenishment-run/${run?.id}/convert`,
          body,
        )
      ).data.data,
    onSuccess: (data) => {
      setResult(data);
      setConverting(false);
      refresh();
    },
    onError,
  });

  if (!run?.id) {
    return null;
  }
  if (isRunning || !ready) {
    return (
      <div style={{ padding: 15 }}>
        <TextV2 variant={"body1"}>
          {t(isRunning ? "suggestions.waiting" : "suggestions.not-ready")}
        </TextV2>
      </div>
    );
  }

  const rows = suggestions.data ?? [];
  const bySupplier = (summary.data ?? []).map((supplier) => ({
    supplier,
    rows: rows.filter((row) => row.supplier_id === supplier.supplier_id),
  }));
  const convertible = rows.filter(
    (row) =>
      (row.status === "open" || row.status === "accepted") &&
      Number(row.final_quantity) > 0,
  );
  const convertibleSuppliers = new Set(
    convertible.map((row) => row.supplier_id),
  ).size;

  const saveQuantity = (
    row: ReplenishmentSuggestionResource,
    value: string,
  ) => {
    if (value === "" || Number(value) === Number(row.final_quantity)) {
      return;
    }
    update.mutate({ id: row.id, body: { final_quantity: value } });
  };

  return (
    <div
      style={{ padding: 15, display: "flex", flexDirection: "column", gap: 15 }}
    >
      <Flex justifyContent={"space-between"} alignItems={"center"}>
        <TextV2 variant={"body1"}>
          {t("suggestions.intro", { count: rows.length })}
        </TextV2>
        <Button
          variant={"filled"}
          disabled={convertible.length === 0 || convert.isPending}
          onClick={() => setConverting(true)}
        >
          {t("convert.button", { count: convertibleSuppliers })}
        </Button>
      </Flex>

      {result && (
        <Alert severity={result.errors.length > 0 ? "warning" : "success"}>
          <Flex
            flexDirection={"column"}
            gap={"4px"}
            style={{ textAlign: "left" }}
          >
            <TextV2 variant={"body1"}>
              {t("convert.result", { count: result.orders.length })}
            </TextV2>
            {result.errors.map((error) => (
              <TextV2 variant={"body1"} key={error.supplier_id}>
                {(rows.find((row) => row.supplier_id === error.supplier_id)
                  ?.supplier?.name ?? error.supplier_id) +
                  ": " +
                  error.message}
              </TextV2>
            ))}
          </Flex>
        </Alert>
      )}

      {rows.length === 0 && (
        <TextV2 variant={"body1"}>{t("suggestions.empty")}</TextV2>
      )}

      {bySupplier.map(({ supplier, rows: supplierRows }) => (
        <Panel key={supplier.supplier_id}>
          <Panel.Header>
            <Panel.Title>
              <Panel.Icon>
                <span className={"icon-15-suppliers"} />
              </Panel.Icon>
              {supplierRows[0]?.supplier?.name ?? supplier.supplier_id}
            </Panel.Title>
            <TextV2
              variant={"body1"}
              style={
                supplier.below_minimum
                  ? { color: "var(--color-danger, #d32f2f)" }
                  : undefined
              }
            >
              {t("suggestions.supplier-sum", {
                amount: money(supplier.amount),
                minimum: money(supplier.minimum_order_value),
              })}
              {supplier.below_minimum
                ? ` · ${t("suggestions.below-minimum")}`
                : ""}
            </TextV2>
          </Panel.Header>
          <Panel.Content>
            <MuiTable size={"small"}>
              <TableHead>
                <TableRow>
                  <TableCell>{t("suggestions.article")}</TableCell>
                  <TableCell>{t("suggestions.stock")}</TableCell>
                  <TableCell>{t("suggestions.open-demand")}</TableCell>
                  <TableCell>{t("suggestions.incoming")}</TableCell>
                  <TableCell>{t("suggestions.available")}</TableCell>
                  <TableCell>{t("suggestions.reporting-stock")}</TableCell>
                  <TableCell>{t("suggestions.suggested")}</TableCell>
                  <TableCell>{t("suggestions.final")}</TableCell>
                  <TableCell>{t("suggestions.price")}</TableCell>
                  <TableCell>{t("suggestions.reasons")}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {supplierRows.map((row) => {
                  const locked = row.status === "converted";
                  const rejected = row.status === "rejected";
                  return (
                    <TableRow
                      key={`${row.id}-${row.final_quantity}-${row.status}`}
                      style={rejected ? { opacity: 0.5 } : undefined}
                    >
                      <TableCell>
                        {row.variant?.sku}
                        <div style={{ opacity: 0.7 }}>{row.variant?.title}</div>
                      </TableCell>
                      <TableCell>{quantity(row.stock_quantity)}</TableCell>
                      <TableCell>
                        {quantity(row.open_demand_quantity)}
                      </TableCell>
                      <TableCell>{quantity(row.incoming_quantity)}</TableCell>
                      <TableCell>{quantity(row.available_quantity)}</TableCell>
                      <TableCell>{quantity(row.reporting_stock)}</TableCell>
                      <TableCell>{quantity(row.suggested_quantity)}</TableCell>
                      <TableCell style={{ width: 110 }}>
                        {locked || rejected ? (
                          quantity(row.final_quantity)
                        ) : (
                          <Input
                            type={"number"}
                            min={0}
                            defaultValue={String(Number(row.final_quantity))}
                            onBlur={(event) =>
                              saveQuantity(row, event.target.value)
                            }
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {row.unit_price === null ? "—" : money(row.unit_price)}
                      </TableCell>
                      <TableCell>
                        {row.reason_codes
                          .map((reason) => t(`reason.${reason}`))
                          .join(", ")}
                      </TableCell>
                      <TableCell>
                        {locked ? (
                          t("suggestions.converted")
                        ) : (
                          <Button
                            size={"sm"}
                            variant={"outlined"}
                            skin={rejected ? "info" : "danger"}
                            disabled={update.isPending}
                            onClick={() =>
                              update.mutate({
                                id: row.id,
                                body: {
                                  status: rejected ? "open" : "rejected",
                                },
                              })
                            }
                          >
                            {rejected
                              ? t("suggestions.restore")
                              : t("suggestions.reject")}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </MuiTable>
          </Panel.Content>
        </Panel>
      ))}

      {converting && (
        <ConvertDialog
          run={run}
          supplierCount={convertibleSuppliers}
          isPending={convert.isPending}
          onSubmit={(body) => convert.mutate(body)}
          onClose={() => setConverting(false)}
        />
      )}
    </div>
  );
};
